import './chatWithSummaryNew.css';
import {
  Box,
  Button,
  Modal,
  SpaceBetween,
  ExpandableSection,
  Container,
  Textarea,
  Multiselect,
  Toggle
} from '@cloudscape-design/components';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UserContext } from '../../../context/authContext';
import {
  IContextBlock,
  IMissingShareObj,
  IPermission,
  outline
} from '../../../lib/models/interfaces';
import {
  extractHtmlAndSnippets,
  extractIdsFromAnswer,
  uniqueById
} from './chatHelpers';
import { CitationText } from './chatAnswer';
import CiteBlockCard from './contextBlockCard';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import {} from '../../../helpers/calculateHoldCoins';
import { ScrollDownArrow } from '../shared/scrollDownArrow';
import { useAlert } from '../../../context/alertContext';
import {
  SearchHistory,
  useSearchHistory
} from '../../../context/searchHistoryContext';
import { KoruLoading } from '../shared/KoruLoading';
import { OptionDefinition } from '@cloudscape-design/components/internal/components/option/interfaces';
import { DocumentContext } from '../../../context/documentContext';
import { ProjectContext } from '../../../context/projectContext';
import ButtonCollection, { ButtonConfig } from '../shared/buttonCollection';
import logger from '../../../helpers/Logger';
export interface ICiteBlock {
  All_Parameters: any[];
  Context: {
    text: string;
    _id: string;
  };
  Document: {
    documentFileName: string;
    _id: string;
  };
  PageNumber?: number;
  pageNumber: number;
  knowledge_base_type: string;
  score: number;
  sentenceId: string;
  text: string;
  _id: string;
  outline?: outline[];
}

export const ChatHistoryViewer = () => {
  const { token, auth, users, user } = useContext(UserContext);
  const { allDocuments } = useContext(DocumentContext);
  const { allFolders, mainProjects, projectMap, folderMap } =
    useContext(ProjectContext);
  const [queryParameters] = useSearchParams();
  const { setAlert } = useAlert();
  const navigate = useNavigate();
  const searchHistory = useSearchHistory();
  const containerRef = useRef<HTMLDivElement>(null);

  const [fullHistory, setFullHistory] = useState<any>();
  const [searchAnswer, setSearchAnswer] = useState<string>(``);
  const [citedBlocksIds, setCitedBlocksIds] = useState<string[]>([]);
  const [snippetInfo, setSnippetInfo] = useState<{ [key: string]: string[] }>();
  const [citeBlocksObject, setCiteBlocksObject] = useState<{
    [key: string]: ICiteBlock;
  }>({});
  const [pinnedCiteBlocks, setPinnedCiteBlocks] = useState<ICiteBlock[]>([]);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [citeBlocks, setCiteBlocks] = useState<ICiteBlock | null>(null);
  const [showShareModel, setShowShareModal] = useState<boolean>(false);
  const [shareOptions, setShareOptions] = useState<OptionDefinition[]>([]);
  const [selectedShareOptions, setSelectedShareOptions] = useState<
    OptionDefinition[]
  >([]);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const [missingShareObj, setMissingShareObj] = useState<IMissingShareObj>();
  const [applyPermissions, setApplyPermissions] = useState<boolean>(false);
  const [shareLoading, setShareLoading] = useState<boolean>(false);
  const [currentHistory, setCurrentHistory] = useState<SearchHistory | null>(
    null
  );
  const [showDocumentsModal, setShowDocumentsModal] = useState<boolean>(false);

  const getHistoryManifestFile = async (history: SearchHistory) => {
    if (!token) return;
    setCurrentHistory(history);
    try {
      console.log('current history', history);
      const presignedUrl = await axios.post(
        `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=getSearchById&auth=self`,
        {
          _id: history?._id,
          requestId: history?.requestId
        },
        auth
      );
      console.log('presigned url', presignedUrl.data.url);
      const response = await axios.get(presignedUrl.data.url, {
        responseType: 'json'
      });
      console.log('response', response.data);
      setFullHistory(response.data);
      //TODO create the relevant info
      getAndSetAnswers(response.data.computedAnswer);
      setCitedInfo(response.data.context);
      setHasFetched(true);
    } catch (e) {
      const error = e as AxiosError;
      setHasFetched(true);
      setCurrentHistory(null);
      setFullHistory(null);
      if (error.status === 403) {
        setAlert(
          'It appears you do not have permision to view this search.',
          'error'
        );
      } else {
        setAlert(error.message, 'error');
      }
    }
  };

  useEffect(() => {
    const historyId = queryParameters.get('historyId');
    if (!token) return;

    setPinnedCiteBlocks([]);
    if (historyId) {
      const thisHistory = searchHistory.history.filter(
        (hist) => hist?._id === historyId
      );
      const thisSharedHistory = searchHistory.sharedHistory.filter(
        (sHist) => sHist?._id === historyId
      );
      getHistoryManifestFile([...thisHistory, ...thisSharedHistory][0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParameters]);

  useEffect(() => {
    const historyId = queryParameters.get('historyId');
    if (token && !hasFetched) {
      if (historyId) {
        const thisHistory = searchHistory.history.filter(
          (hist) => hist?._id === historyId
        );
        if (thisHistory.length === 0) return;

        getHistoryManifestFile(thisHistory[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, hasFetched]);

  const getAndSetAnswers = (computedAnswer: { text: string }[]) => {
    const answersMapped = computedAnswer.map(
      (ele: { text: string }) => ele.text
    );
    const answerInfo = extractHtmlAndSnippets(answersMapped.join('\n\n'));
    console.log('parsedSnippets', answerInfo.parsedSnippets);
    console.log('Html before snippets', answerInfo.htmlBeforeSnippets);

    setSnippetInfo(answerInfo.parsedSnippets);

    setSearchAnswer(answerInfo.htmlBeforeSnippets);

    const getContextIds = extractIdsFromAnswer(answersMapped.join('\n\n'));
    // parse the snippets from the computed
    setCitedBlocksIds(getContextIds);
  };

  const handleCite = (id: string) => {
    const citeBlock = citeBlocksObject[id];
    console.log('cite block', citeBlock);
    setCiteBlocks(citeBlock);
    setShowModal(true);
  };

  const pinCiteBlock = (id: string, pinAdd: boolean) => {
    const citeBlock = citeBlocksObject[id];
    // console.log('cite block', citeBlock);
    if (pinAdd) {
      const isBlockAlreadyPinned = pinnedCiteBlocks.some(
        (block) => block._id === citeBlock._id
      );
      if (!isBlockAlreadyPinned) {
        setPinnedCiteBlocks((prevState) => [...prevState, citeBlock]);
      }
    }
    if (!pinAdd) {
      const updatedPinnedCiteBlocks = pinnedCiteBlocks.filter(
        (block) => block._id !== citeBlock._id
      );
      setPinnedCiteBlocks(updatedPinnedCiteBlocks);
    }
  };
  const setCitedInfo = (contexts: IContextBlock[][]) => {
    const allObjects: IContextBlock[] = contexts.flat();
    const uniqueContexts = uniqueById(allObjects);

    const answerObject = uniqueContexts.reduce(
      (acc, curr) => {
        acc[curr._id] = curr;
        return acc;
      },
      {} as { [key: string]: any }
    );
    setCiteBlocksObject(answerObject);
  };
  const handleDelete = async () => {
    const historyId = queryParameters.get('historyId');
    if (!historyId || !token) return;
    searchHistory.deleteById(historyId);
  };

  function getMissingStrings(
    array1: OptionDefinition[],
    objects: IPermission[]
  ): OptionDefinition[] {
    const subSet = new Set(objects.map((o) => o.sub));
    return array1.filter((item) => !subSet.has(item?.value!));
  }

  const checkMissingPermissions = () => {
    console.log('full history', fullHistory);
    if (!fullHistory) return;

    const relevantDocs = allDocuments.filter((ele) =>
      fullHistory.documentIds.includes(ele._id)
    );
    // create share options for each doc.
    console.log('relevant docs', relevantDocs);
    // const selectedUsers = selectedShareOptions.map((sub) => sub.value);

    const shareObj = relevantDocs.reduce<IMissingShareObj>((acc, curr) => {
      // check if all share users have permission on current doc.
      if (curr.searchAble) {
        return acc;
      }
      const currentDocMissing = getMissingStrings(
        selectedShareOptions.filter((sOption) => !sOption.tags),
        curr.permissions
      );
      if (currentDocMissing.length > 0) {
        const folderName =
          allFolders.filter((f) => f._id === curr.folder)[0]?.folderName ?? '';
        const projectName =
          mainProjects.filter((p) => p._id === curr.project)[0].projectName ??
          '';
        acc[curr._id] = {
          documentFileName: curr.documentFileName,
          folderName: folderName,
          projectName: projectName,
          missingShare: currentDocMissing
        };
      }
      return acc;
    }, {} as IMissingShareObj);
    console.log('shareObj', shareObj);
    if (Object.keys(shareObj).length > 0) {
      setMissingShareObj(shareObj);
    } else {
      setMissingShareObj({} as IMissingShareObj);
    }
  };

  useEffect(() => {
    checkMissingPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShareOptions]);

  const modelIdText = (mId: string | undefined | null) => {
    if (!mId) return 'Deepseek';
    switch (true) {
      case mId ===
        'arn:aws:bedrock:us-west-2:674674125182:inference-profile/us.deepseek.r1-v1:0':
        return 'Deepseek';
      case mId === 'openai.gpt-oss-120b-1:0':
        return 'Open AI 120b';
      default:
        return 'Deepseek';
    }
  };

  const shareSearch = async () => {
    if (!currentHistory) return;
    try {
      setShareLoading(true);
      const newSubsArray = selectedShareOptions.map((e) => e.value);
      const reqBody: any = {
        _id: currentHistory._id,
        subsArray: newSubsArray
      };
      // check to see if there are any users exiting that are removed?

      if (applyPermissions && missingShareObj) {
        const documentUpdateInfo: any[] = [];
        const documentKeys = Object.keys(missingShareObj);
        for (const key of documentKeys) {
          const tempInfoItem: any = {};
          tempInfoItem.documentId = key;
          tempInfoItem.permissionsToAdd = missingShareObj[key].missingShare.map(
            (ele) => {
              return {
                sub: ele.value,
                name: ele.label,
                action: 'view'
              };
            }
          );
          documentUpdateInfo.push(tempInfoItem);
        }
        reqBody.documentUpdateInfo = documentUpdateInfo;
        reqBody.grantDocumentPermissions = true;
      }
      const result = await axios.post(
        `${process.env.REACT_APP_KORU_API}/share?type=searchHistory`,
        reqBody,
        auth
      );
      // update the record for the search history.
      const temp = searchHistory.history;
      const findIndex = temp.findIndex(
        (hist) => hist._id === result.data.result._id
      );
      if (findIndex !== -1) {
        temp[findIndex] = result.data.result;
        searchHistory.setHistory([...temp]);
      }
      console.log('share result', result);
      console.log('reqBody', reqBody);
      setAlert('Search Shared', 'success', 3);
      setShareLoading(false);
      setShowShareModal(false);
    } catch (e) {
      const error = e as Error;
      setAlert(error.message, 'error');
      setShareLoading(false);
      setShowShareModal(false);
    }
  };
  const removeUserForDocument = (
    documentId: string,
    user: OptionDefinition
  ) => {
    if (missingShareObj && missingShareObj[documentId]) {
      const temp = missingShareObj;
      const newPermissions = temp[documentId].missingShare.filter(
        (od) => od.value !== user.value
      );
      if (newPermissions.length === 0) {
        delete temp[documentId];
      } else {
        temp[documentId].missingShare = newPermissions;
      }

      setMissingShareObj({ ...temp });
    }
  };

  useMemo(() => {
    if (currentHistory && showShareModel) {
      logger.info('current history', currentHistory);
      const existingShares = [];
      for (const sub of currentHistory?.shared) {
        const [user] = users.filter((u) => u.sub === sub);
        existingShares.push({
          label: user.name,
          value: user.sub,
          tags: ['Current']
        });
      }
      setSelectedShareOptions(existingShares);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showShareModel, currentHistory]);

  // useEffect(() => {
  //   if (showShareModel) {
  //     loadExistingShares();
  //   }
  // }, [showShareModel]);

  const handleRemove = async () => {
    try {
      const result = await axios.post(
        `${process.env.REACT_APP_KORU_API}/searchHistory?type=remove`,
        {
          _id: currentHistory?._id
        },
        auth
      );
      const temp = searchHistory.sharedHistory.filter(
        (shist) => shist._id !== currentHistory?._id!
      );
      searchHistory.setSharedHistory(temp);
      navigate('/app/landing');
    } catch (e) {
      const error = e as Error;
      setAlert(error.message, 'error');
    }
  };

  const buttonsInfo: ButtonConfig[] = useMemo(
    () => {
      const buttonArray: ButtonConfig[] = [];
      if (currentHistory && currentHistory.sub === user.sub) {
        buttonArray.push(
          {
            label: `Delete`,
            buttonFunctions: () => handleDelete(),
            type: 'main',
            variant: 'primary',
            disabled: false
          },
          {
            label: `Share`,
            buttonFunctions: () => setShowShareModal(true),
            type: 'main',
            variant: 'primary',
            disabled: false
          },
          {
            label: `Documents`,
            buttonFunctions: () => setShowDocumentsModal(true),
            type: 'additional',
            variant: 'normal',
            disabled: false
          }
        );
      }
      if (currentHistory && currentHistory?.shared?.includes(user.sub)) {
        buttonArray.push({
          label: `Remove`,
          buttonFunctions: () => handleRemove(),
          type: 'main',
          variant: 'primary',
          disabled: false
        });
      }
      // if (
      //   currentHistory &&
      //   currentHistory?.shared.length > 0 &&
      //   currentHistory.sub === user.sub
      // ) {
      //   currentHistory.shared.forEach((share) => {
      //     buttonArray.push({
      //       label: `${users.filter((user) => share === user.sub)[0].name}`,
      //       buttonFunctions: () => {},
      //       type: 'additional',
      //       variant: 'normal',
      //       disabled: false
      //     });
      //   });
      // }
      return buttonArray;
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentHistory, user.sub]
  );

  return (
    <div>
      <div
        className="relative h-screen overflow-y-auto pb-6 p-4"
        style={{ scrollbarWidth: 'none' }}
        ref={containerRef}
      >
        <div className="relative p-4">
          <div className="absolute top-[2%] right-[2%] space-x-2">
            <ButtonCollection
              buttons={buttonsInfo}
              sharedWith={
                currentHistory && currentHistory?.shared?.length > 0
                  ? true
                  : undefined
              }
              sharedWithArray={
                currentHistory?.sub === user.sub
                  ? currentHistory?.shared
                  : undefined
              }
            />
          </div>
          <SpaceBetween size={'s'} data-id="element-to_print">
            <div className="pb-6 border-gray-200">
              <h1 className="text-3xl font-bold text-gray-900 mb-3">
                KORU AI Assistant
              </h1>
              <div className="max-w-3xl mb-0">
                <p className="text-gray-600">Review Previous AI Findings</p>
              </div>
            </div>

            {fullHistory && searchAnswer?.length > 0 && (
              <div className="p-4">
                <Textarea value={fullHistory?.originalQuestion} rows={3} />
                <div className="mt-4 p-4 bg-white rounded-2xl shadow-xl flex flex-col">
                  <h3 className="text-xl">AI Findings</h3>
                  <CitationText
                    text={searchAnswer}
                    onCiteClick={handleCite}
                    prevAnswer={true}
                  />
                </div>
              </div>
            )}
            {fullHistory && searchAnswer === '' && (
              <Container>
                <SpaceBetween direction="vertical" size={'xs'}>
                  <Box variant="p">
                    There appear to be no results for your search, please try a
                    different search term or change the scope of your search.
                  </Box>
                </SpaceBetween>
              </Container>
            )}

            {pinnedCiteBlocks.length > 0 && (
              <div className="p-4">
                <div className="p-4 bg-white rounded-2xl shadow-xl flex flex-col ">
                  <SpaceBetween direction="vertical" size={'xs'}>
                    <ExpandableSection
                      key={'expand-3'}
                      headerText={`Saved Citations ${pinnedCiteBlocks.length.toString()}`}
                    >
                      {pinnedCiteBlocks.map((ele) => {
                        return (
                          <CiteBlockCard
                            key={ele._id}
                            citeBlock={ele}
                            auth={auth}
                            pinCiteBlock={pinCiteBlock}
                            pinAdd={false}
                            view={''}
                            goToSpecificPage={() => {}}
                            closeModal={setShowModal}
                          />
                        );
                      })}
                    </ExpandableSection>
                  </SpaceBetween>
                </div>
              </div>
            )}

            {citedBlocksIds.length > 0 ? (
              <div className="p-4">
                <div className="p-4 bg-white rounded-2xl shadow-xl flex flex-col ">
                  <SpaceBetween direction="vertical" size={'xs'}>
                    <ExpandableSection
                      key={'expand-1'}
                      headerText="Cited Information"
                      defaultExpanded={false}
                    >
                      {citedBlocksIds
                        ?.filter((id) => citeBlocksObject[id] !== undefined)
                        .map((id) => citeBlocksObject[id])
                        .map((citeBlock) => (
                          <CiteBlockCard
                            key={citeBlock._id}
                            citeBlock={citeBlock}
                            auth={auth}
                            pinCiteBlock={pinCiteBlock}
                            pinAdd={true}
                            view={''}
                            goToSpecificPage={() => {}}
                            closeModal={setShowModal}
                          />
                        ))}
                    </ExpandableSection>
                    <ExpandableSection
                      key={'expand-2'}
                      headerText="Additional Relevant Information"
                      defaultExpanded={false}
                    >
                      {Object.entries(citeBlocksObject)
                        .filter(([key]) => !citedBlocksIds.includes(key))
                        .map(([_, value]) => value)
                        .map((citeBlock) => (
                          <CiteBlockCard
                            key={citeBlock._id}
                            citeBlock={citeBlock}
                            auth={auth}
                            pinCiteBlock={pinCiteBlock}
                            pinAdd={true}
                            view={''}
                            goToSpecificPage={() => {}}
                            closeModal={setShowModal}
                          />
                        ))}
                    </ExpandableSection>
                  </SpaceBetween>
                </div>
              </div>
            ) : null}
          </SpaceBetween>
        </div>
        {!fullHistory && !hasFetched && (
          // <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          //   <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          // </div>
          <KoruLoading centerText="Loading Search" />
        )}
        <Modal
          visible={showModal}
          onDismiss={() => {
            setShowModal(false);
            setCiteBlocks(null);
          }}
          size="max"
        >
          {citeBlocks && (
            <CiteBlockCard
              citeBlock={citeBlocks}
              auth={auth}
              pinCiteBlock={pinCiteBlock}
              pinAdd={true}
              view={''}
              goToSpecificPage={() => {}}
              closeModal={setShowModal}
              docIndex={0}
              showSearchBox={true}
              snippetInfo={snippetInfo}
            />
          )}
        </Modal>
        <Modal
          size="large"
          visible={showShareModel}
          onDismiss={() => setShowShareModal(false)}
          header={'Share Document'}
          footer={
            <div className="w-full flex justify-between">
              <>
                <div className="space-x-2">
                  <Button
                    onClick={() => {
                      setShowShareModal(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={
                      missingShareObj &&
                      Object.keys(missingShareObj).length > 0 &&
                      !applyPermissions
                    }
                    loading={shareLoading}
                    onClick={() => shareSearch()}
                  >
                    Share Search
                  </Button>
                </div>
                <div>
                  <Toggle
                    checked={applyPermissions}
                    onChange={({ detail }) =>
                      setApplyPermissions(detail.checked)
                    }
                  >
                    Grant Necessary Permissions
                  </Toggle>
                </div>
              </>
            </div>
          }
        >
          <div
            style={{
              height: `${missingShareObj ? '55vh' : '40vh'}`
            }}
          >
            <Multiselect
              onChange={({ detail }) =>
                setSelectedShareOptions([...detail.selectedOptions])
              }
              selectedOptions={selectedShareOptions}
              options={users
                .filter((u) => u.sub !== user.sub)
                .map((ele) => {
                  return {
                    label: ele.name,
                    value: ele.sub
                    // disabled: ele.sub === user.sub
                  };
                })}
            ></Multiselect>
            {missingShareObj && Object.keys(missingShareObj).length > 0 && (
              <div className="p-4 max-h-[400px] overflow-y-scroll">
                <p className="text-xl py-2">
                  The following users do not have access to the source
                  documents. Documents needing permissions are listed.
                </p>
                <div className="space-y-2">
                  {Object.keys(missingShareObj).map((ele) => {
                    return (
                      <div className="p-2 border border-grey-500 rounded-md">
                        <div className="italic p-2">
                          {/* <p>{missingShareObj[ele].documentFileName}</p> */}
                          <div className="p-2">
                            <span className="px-2 py-1 bg-gray-100 rounded-md">
                              {missingShareObj[ele].projectName}
                            </span>
                            <span className="px-2 text-gray-400">&gt;</span>
                            <span className="px-2 py-1 bg-gray-100 rounded-md">
                              {missingShareObj[ele].folderName}
                            </span>
                            <span className="px-2 text-gray-400">&gt;</span>
                            <span className="px-2 py-1 bg-gray-100 rounded-md">
                              {missingShareObj[ele].documentFileName}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-4">
                          {missingShareObj[ele].missingShare.map((p) => (
                            <div className="p-2 border border-[#56dac5] rounded-md bg-[#56dac5]/20 inline-block">
                              <span>{p.label}</span>
                              <span
                                className="ml-2 text-blue-800"
                                onClick={() => removeUserForDocument(ele, p)}
                              >
                                X
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </Modal>
        <Modal
          visible={showDocumentsModal}
          header={<div>Search Scope</div>}
          size="large"
          onDismiss={() => setShowDocumentsModal(false)}
        >
          <div className="w-full">
            {currentHistory?.Documents.map((doc) => {
              return (
                <div className="p-2 w-full">
                  <span className="px-2 py-1 bg-gray-100 rounded-md">
                    {projectMap[doc.project]}
                  </span>
                  <span className="px-2 text-gray-400">&gt;</span>
                  <span className="px-2 py-1 bg-gray-100 rounded-md">
                    {folderMap[doc.folder]}
                  </span>
                  <span className="px-2 text-gray-400">&gt;</span>
                  <span className="px-2 py-1 bg-gray-100 rounded-md">
                    {doc.documentFileName}
                  </span>
                </div>
              );
            })}
          </div>
        </Modal>

        <ScrollDownArrow
          containerRef={containerRef as React.RefObject<HTMLElement>}
          bottomValue="13%"
        />
      </div>
    </div>
  );
};
